import React, {
  useState,
  useEffect,
  useRef,
  ComponentPropsWithoutRef,
  PropsWithChildren,
} from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { LpgCancelOutline } from "@/icons";

import { ReactDefaultProps } from "@/types/react";
import { Theme, ThemeProvider } from "../Theme";
import { A11yText } from "../A11yText";
import {
  Button,
  ButtonProps,
  ButtonDefaultTag,
  ButtonDefaultTagName,
} from "../Button";
import { Portal } from "../Portal";
import { Icon } from "../Icon";

import styles from "./Modal.scss";

export type ModalPortalBaseProps<
  ButtonTag extends React.ElementType = ButtonDefaultTag
> = PropsWithChildren<{
  zIndex: number;
  isOpen?: boolean;
  isClosing: boolean;
  isVisible?: boolean;
  renderClosed?: boolean;
  closable?: boolean;
  closeText?: string;
  trigger?: Element;
  beforeOpen?: () => void;
  afterClose?: () => void;
  portalless?: boolean;
  containerProps?: { customClass?: string } & Omit<
    ComponentPropsWithoutRef<"div">,
    "customClass"
  >;
  innerContainerProps?: { customClass?: string } & Omit<
    ComponentPropsWithoutRef<"div">,
    "customClass"
  >;
  contentProps?: { customClass?: string } & Omit<
    ComponentPropsWithoutRef<"div">,
    "customClass"
  >;
  buttonProps?: ButtonProps<ButtonTag>;
  customClass?: string;
}>;

export type ModalPortalProps<
  ButtonTag extends React.ElementType = ButtonDefaultTag
> = ModalPortalBaseProps<ButtonTag> &
  Omit<ComponentPropsWithoutRef<"div">, keyof ModalPortalBaseProps<ButtonTag>>;

const defaultProps = {
  isOpen: true,
  isVisible: false,
  renderClosed: false,
  closable: true,
  closeText: "Schließen",
  trigger: undefined,
  beforeOpen: undefined,
  afterClose: undefined,
  portalless: false,
  containerProps: undefined,
  innerContainerProps: undefined,
  contentProps: undefined,
  buttonProps: undefined,
  customClass: undefined,
  children: null,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ModalPortalBaseProps,
  typeof defaultProps
> = defaultProps;

export const ModalPortal = <
  ButtonTag extends React.ElementType = ButtonDefaultTag
>(
  props: ModalPortalProps<ButtonTag>
): JSX.Element => {
  const {
    zIndex,
    isVisible: isVisibleProp,
    isOpen,
    isClosing,
    renderClosed,
    closable,
    closeText,
    trigger = typeof document !== "undefined"
      ? document.activeElement
      : undefined,
    beforeOpen,
    afterClose: afterCloseProp,
    portalless,
    children,
    containerProps,
    innerContainerProps,
    contentProps,
    buttonProps = defaultProps.buttonProps,
    customClass,
    ...others
  } = props;
  const [isVisible, setIsVisible] = useState(isVisibleProp);
  useEffect(() => {
    setIsVisible(isVisibleProp);
  }, [isVisibleProp]);
  const { tag: buttonTagProp, ...buttonRestProps } = buttonProps || {
    tag: ButtonDefaultTagName,
  };
  const buttonTag =
    (buttonTagProp as ButtonProps["tag"]) || ButtonDefaultTagName;
  const PortalTag = portalless ? "div" : Portal;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const { customClass: containerCustomClass, ...containerOtherProps } =
    containerProps || {};
  const {
    customClass: innerContainerCustomClass,
    ...innerContainerOtherProps
  } = innerContainerProps || {};
  const { customClass: contentCustomClass, ...contentOtherProps } =
    contentProps || {};

  const afterClose = () => {
    modalRef.current?.removeEventListener("transitionend", afterClose);
    afterCloseProp?.();
  };

  const close = () => {
    modalRef.current?.addEventListener("transitionend", afterClose);
    setIsVisible(false);
  };

  const checkOverlayClick = (event: MouseEvent) => {
    if (!closable) {
      return;
    }
    if (
      event.target instanceof Node &&
      !modalRef.current?.contains(event.target)
    ) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const checkDocumentKeyDown = (event: KeyboardEvent) => {
    if (!closable) {
      return;
    }
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      close();
    }
  };

  const handleTabKey = (event: KeyboardEvent) => {
    if (!isVisible) {
      return true;
    }

    if (modalRef.current) {
      const focusableModalElements = modalRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );
      const firstElement = focusableModalElements[0];
      const lastElement =
        focusableModalElements[focusableModalElements.length - 1];

      if (
        event.shiftKey &&
        event.code === "Tab" &&
        document.activeElement === firstElement
      ) {
        if (lastElement instanceof HTMLElement) lastElement.focus();
        return event.preventDefault();
      }

      if (
        !event.shiftKey &&
        event.code === "Tab" &&
        document.activeElement === lastElement
      ) {
        if (firstElement instanceof HTMLElement) firstElement.focus();
        event.preventDefault();
      }
    }
    return true;
  };

  useEffect(() => {
    beforeOpen?.();
    modalRef.current?.focus();
    wrapperRef.current?.addEventListener("click", checkOverlayClick);
    document.addEventListener("keydown", checkDocumentKeyDown);
    document.addEventListener("keydown", handleTabKey);
    setIsVisible(true);
    return () => {
      if (trigger instanceof HTMLElement)
        trigger.focus({ preventScroll: true });
      wrapperRef.current?.removeEventListener("click", checkOverlayClick);
      document.removeEventListener("keydown", handleTabKey);
      document.removeEventListener("keydown", checkDocumentKeyDown);
    };
  }, [wrapperRef.current, modalRef.current]);

  useEffect(() => {
    if (!isOpen) {
      close();
    }
    if (isOpen && !isVisibleProp) {
      setIsVisible(true);
    }
  }, [isOpen, isVisibleProp]);

  return (
    <PortalTag>
      <ThemeProvider value={Theme.light}>
        <div
          className={cn(styles.modal, containerCustomClass, {
            "is-visible": isVisible && isOpen,
          })}
          ref={wrapperRef}
          style={{ zIndex: (isVisible && isOpen) || isClosing ? zIndex : -1 }}
          {...containerOtherProps}
        >
          <div
            className={cn(styles.cover, {
              "is-visible": isVisible && isOpen,
            })}
          />
          <div
            className={cn(styles.container, innerContainerCustomClass)}
            {...innerContainerOtherProps}
          >
            <div
              className={cn(styles.inner, customClass, {
                "is-visible": isVisible && isOpen,
              })}
              role="dialog"
              aria-modal="true"
              ref={modalRef}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              {...others}
            >
              {closable && (
                <Button
                  tag={buttonTag}
                  variants={[Button.Variant.clean, Button.Variant.rectangle]}
                  customClass={styles.close}
                  type="button"
                  onClick={close}
                  {...buttonRestProps}
                >
                  <Icon icon={LpgCancelOutline} size={Icon.Size.medium} />
                  <A11yText>{closeText}</A11yText>
                </Button>
              )}

              <div
                className={cn(styles.content, contentCustomClass)}
                {...contentOtherProps}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </PortalTag>
  );
};

ModalPortal.defaultProps = validatedDefaultProps;

ModalPortal.propTypes = {
  isOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  renderClosed: PropTypes.bool,
  closable: PropTypes.bool,
  closeText: PropTypes.string,
  trigger:
    typeof Element !== "undefined"
      ? PropTypes.instanceOf(Element)
      : (props: ModalPortalProps, propName: keyof ModalPortalProps) => {
          return props[propName] === defaultProps.trigger
            ? null
            : Error(`${propName} must be default`);
        },
  beforeOpen: PropTypes.func,
  afterClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  portalless: PropTypes.bool,
  containerProps: PropTypes.objectOf(PropTypes.any),
  buttonProps: PropTypes.objectOf(PropTypes.any),
  zIndex: PropTypes.number.isRequired,
};

ModalPortal.displayName = "ModalPortal";

export default ModalPortal;
